import { useState, useEffect, useMemo } from 'react';
import { useI18n } from '../../../../../i18n';
import { fetchOrganizaciones, type OrganizacionResumen } from '../../../../shared/services/api';

interface OrgNode extends OrganizacionResumen {
  organizacionPadreId: string | null;
  participacionEnPadre: number | string | null;
  nif: string | null;
  pais: { nombre: string } | string | null;
  children: OrgNode[];
}

const BADGE_COLORS: Record<string, { bg: string; text: string }> = {
  HOLDING: { bg: '#1F3864', text: '#fff' },
  EMPRESA: { bg: '#2F5496', text: '#fff' },
  FILIAL: { bg: '#33BCE1', text: '#fff' },
  SUCURSAL: { bg: '#4472C4', text: '#fff' },
};

const BADGE_DEFAULT = { bg: '#E5E7EB', text: '#374151' };

function getBadgeColor(tipo: string) {
  return BADGE_COLORS[tipo] ?? BADGE_DEFAULT;
}

function buildTree(orgs: OrgNode[]): OrgNode[] {
  const map = new Map<string, OrgNode>();
  for (const org of orgs) {
    map.set(org.id, { ...org, children: [] });
  }
  const roots: OrgNode[] = [];
  for (const org of map.values()) {
    if (org.organizacionPadreId && map.has(org.organizacionPadreId)) {
      map.get(org.organizacionPadreId)!.children.push(org);
    } else {
      roots.push(org);
    }
  }
  return roots;
}

function formatParticipacion(val: number | string | null | undefined): string | null {
  if (val == null) return null;
  const num = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(num)) return null;
  return `${num}%`;
}

function getPaisName(pais: { nombre: string } | string | null | undefined): string | null {
  if (!pais) return null;
  if (typeof pais === 'string') return pais;
  return pais.nombre ?? null;
}

const styles = {
  container: {
    width: '100%',
    overflowX: 'auto' as const,
    padding: '24px 0',
  },
  treeWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    minWidth: 'fit-content',
  },
  childrenRow: {
    display: 'flex',
    justifyContent: 'center',
    gap: 0,
    position: 'relative' as const,
  },
  childWrapper: {
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    position: 'relative' as const,
  },
  node: {
    background: '#fff',
    border: '1px solid #E2E6ED',
    borderRadius: 10,
    padding: '14px 18px',
    minWidth: 180,
    textAlign: 'center' as const,
    position: 'relative' as const,
    cursor: 'default',
    transition: 'box-shadow 0.15s ease',
  },
  nodeHover: {
    boxShadow: '0 4px 16px rgba(31, 56, 100, 0.15)',
  },
  nodeName: {
    fontSize: 14,
    fontWeight: 700,
    color: '#1A1D23',
    margin: 0,
    lineHeight: 1.3,
    fontFamily: 'inherit',
  },
  badge: (bg: string, text: string): React.CSSProperties => ({
    display: 'inline-block',
    fontSize: 10,
    fontWeight: 600,
    fontFamily: 'inherit',
    padding: '2px 8px',
    borderRadius: 4,
    marginTop: 6,
    background: bg,
    color: text,
    letterSpacing: 0.5,
    textTransform: 'uppercase',
  }),
  participacion: {
    fontSize: 11,
    fontWeight: 600,
    color: '#2F5496',
    background: '#F0F4FA',
    borderRadius: 10,
    padding: '1px 8px',
    position: 'absolute' as const,
    top: -10,
    left: '50%',
    transform: 'translateX(-50%)',
    whiteSpace: 'nowrap' as const,
    zIndex: 2,
  },
  verticalLine: {
    width: 0,
    height: 28,
    borderLeft: '2px solid #D1D5DB',
    margin: '0 auto',
  },
  horizontalBar: {
    height: 0,
    borderTop: '2px solid #D1D5DB',
    position: 'absolute' as const,
    top: 0,
  },
  tooltip: {
    position: 'absolute' as const,
    bottom: '100%',
    left: '50%',
    transform: 'translateX(-50%)',
    marginBottom: 8,
    background: '#1F3864',
    color: '#fff',
    fontSize: 11,
    fontFamily: 'inherit',
    padding: '8px 12px',
    borderRadius: 6,
    whiteSpace: 'nowrap' as const,
    zIndex: 10,
    pointerEvents: 'none' as const,
    boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
    lineHeight: 1.5,
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    padding: 40,
    color: '#6B7280',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  empty: {
    textAlign: 'center' as const,
    padding: 40,
    color: '#9CA3AF',
    fontSize: 14,
    fontFamily: 'inherit',
  },
  title: {
    fontSize: 16,
    fontWeight: 700,
    color: '#1F3864',
    marginBottom: 20,
    textAlign: 'center' as const,
    fontFamily: 'inherit',
  },
};

interface TreeNodeProps {
  node: OrgNode;
  depth: number;
  maxDepth: number;
  tipoLabels: Record<string, string>;
  nifLabel: string;
  paisLabel: string;
}

function TreeNode({ node, depth, maxDepth, tipoLabels, nifLabel, paisLabel }: TreeNodeProps) {
  const [hovered, setHovered] = useState(false);
  const badge = getBadgeColor(node.tipo);
  const participacion = formatParticipacion(node.participacionEnPadre);
  const paisName = getPaisName(node.pais);
  const showTooltip = hovered && (node.nif || paisName);
  const visibleChildren = depth < maxDepth ? node.children : [];

  return (
    <div style={styles.childWrapper}>
      {participacion && (
        <div style={{ position: 'relative', height: 0 }}>
          <span style={styles.participacion}>{participacion}</span>
        </div>
      )}
      <div
        style={{
          ...styles.node,
          ...(hovered ? styles.nodeHover : {}),
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {showTooltip && (
          <div style={styles.tooltip}>
            {node.nif && (
              <div>
                {nifLabel}: {node.nif}
              </div>
            )}
            {paisName && (
              <div>
                {paisLabel}: {paisName}
              </div>
            )}
          </div>
        )}
        <p style={styles.nodeName}>{node.nombre}</p>
        <span style={styles.badge(badge.bg, badge.text)}>{tipoLabels[node.tipo] ?? node.tipo}</span>
      </div>

      {visibleChildren.length > 0 && (
        <>
          <div style={styles.verticalLine} />
          <ChildrenRow
            children={visibleChildren}
            depth={depth + 1}
            maxDepth={maxDepth}
            tipoLabels={tipoLabels}
            nifLabel={nifLabel}
            paisLabel={paisLabel}
          />
        </>
      )}
    </div>
  );
}

interface ChildrenRowProps {
  children: OrgNode[];
  depth: number;
  maxDepth: number;
  tipoLabels: Record<string, string>;
  nifLabel: string;
  paisLabel: string;
}

function ChildrenRow({
  children,
  depth,
  maxDepth,
  tipoLabels,
  nifLabel,
  paisLabel,
}: ChildrenRowProps) {
  if (children.length === 0) return null;

  return (
    <div style={{ position: 'relative' }}>
      {children.length > 1 && <HorizontalBar count={children.length} />}
      <div
        style={{
          ...styles.childrenRow,
          paddingTop: children.length > 1 ? 0 : 0,
        }}
      >
        {children.map((child) => (
          <div
            key={child.id}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              padding: '0 16px',
            }}
          >
            <div style={styles.verticalLine} />
            <TreeNode
              node={child}
              depth={depth}
              maxDepth={maxDepth}
              tipoLabels={tipoLabels}
              nifLabel={nifLabel}
              paisLabel={paisLabel}
            />
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBar({ count }: { count: number }) {
  if (count <= 1) return null;

  const pct = ((count - 1) / count) * 100;
  const offset = (100 - pct) / 2;

  return (
    <div
      style={{
        ...styles.horizontalBar,
        left: `${offset}%`,
        width: `${pct}%`,
      }}
    />
  );
}

function collectTypes(nodes: OrgNode[]): string[] {
  const types = new Set<string>();
  function walk(node: OrgNode) {
    types.add(node.tipo);
    for (const child of node.children) walk(child);
  }
  for (const n of nodes) walk(n);
  return Array.from(types);
}

function filterTree(nodes: OrgNode[], tipos: string[]): OrgNode[] {
  if (tipos.length === 0) return nodes;
  function prune(node: OrgNode): OrgNode | null {
    if (tipos.includes(node.tipo)) return node;
    const kept = node.children.map(prune).filter(Boolean) as OrgNode[];
    if (kept.length === 0) return null;
    return { ...node, children: kept };
  }
  return nodes.map(prune).filter(Boolean) as OrgNode[];
}

export function OrganizacionesArbol() {
  const { t } = useI18n();
  const [orgs, setOrgs] = useState<OrgNode[]>([]);
  const [loading, setLoading] = useState(true);
  const [tiposFiltro, setTiposFiltro] = useState<string[]>([]);

  const tipoLabels: Record<string, string> = useMemo(
    () => ({
      HOLDING: t.structure.orgs.holding,
      EMPRESA: t.structure.orgs.empresa,
      FILIAL: t.structure.orgs.filial,
      SUCURSAL: t.structure.orgs.sucursal,
      DIVISION: t.structure.orgs.division,
      UNIDAD_NEGOCIO: t.structure.orgs.unidadNegocio,
      UTE: t.structure.orgs.ute,
      JOINT_VENTURE: t.structure.orgs.jointVenture,
      CONCESION: t.structure.orgs.concesion,
    }),
    [t],
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchOrganizaciones()
      .then((res) => {
        if (!cancelled) {
          const raw = (res.data ?? []) as unknown as OrgNode[];
          setOrgs(raw);
        }
      })
      .catch(() => {
        if (!cancelled) setOrgs([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tree = useMemo(() => buildTree(orgs), [orgs]);

  const uniqueTypes = useMemo(() => collectTypes(tree), [tree]);

  const filteredTree = useMemo(() => filterTree(tree, tiposFiltro), [tree, tiposFiltro]);

  const toggleTipo = (tipo: string) => {
    setTiposFiltro((prev) =>
      prev.includes(tipo) ? prev.filter((t) => t !== tipo) : [...prev, tipo],
    );
  };

  if (loading) {
    return <div style={styles.loading}>{t.structure.orgs.loadingOrgs}</div>;
  }

  if (tree.length === 0) {
    return <div style={styles.empty}>{t.structure.orgs.noOrgs}</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.title}>{t.structure.admin.arbolSocietario}</div>
      {uniqueTypes.length >= 2 && (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 6, marginBottom: 16 }}>
          {uniqueTypes.map((tipo) => {
            const active = tiposFiltro.includes(tipo);
            const badge = getBadgeColor(tipo);
            return (
              <button
                key={tipo}
                type="button"
                onClick={() => toggleTipo(tipo)}
                style={{
                  padding: '4px 12px',
                  borderRadius: 12,
                  fontSize: 11,
                  cursor: 'pointer',
                  border: 'none',
                  background: active ? badge.bg : '#F3F4F6',
                  color: active ? '#fff' : '#6B7280',
                  fontWeight: active ? 600 : 400,
                  fontFamily: 'inherit',
                }}
              >
                {tipoLabels[tipo] ?? tipo}
              </button>
            );
          })}
        </div>
      )}
      <div style={styles.treeWrapper}>
        {filteredTree.map((root) => (
          <TreeNode
            key={root.id}
            node={root}
            depth={0}
            maxDepth={3}
            tipoLabels={tipoLabels}
            nifLabel={t.structure.orgs.nifCif}
            paisLabel={t.structure.orgs.country}
          />
        ))}
      </div>
    </div>
  );
}
